import {
    Box,
    Text,
    Grid,
    Flex,
} from "@chakra-ui/react";
import {
    Upload,
    X,
    FileText,
} from "lucide-react";
import {
    useRef,
    useEffect,
} from "react";

const AttachmentUpload = ({
    files = [],
    onChange,
    maxFiles = 6,
    accept = "*",
    title = "Attachments",
    description = "Upload files",
}) => {
    const inputRef = useRef(null);

    useEffect(() => {
        return () => {
            files.forEach((item) => {
                if (item.preview) {
                    URL.revokeObjectURL(item.preview);
                }
            });
        };
    }, [files]);

const handleChange = (e) => {
    const selected = Array.from(
        e.target.files || []
    );

    const processed = selected.map(
        (file) => ({
            file,
            preview: file.type.startsWith(
                "image/"
            )
                ? URL.createObjectURL(
                      file
                  )
                : null,
        })
    );

    // Single file mode → replace existing file
    if (maxFiles === 1) {
        // cleanup old preview
        files.forEach((item) => {
            if (
                item.preview
            ) {
                URL.revokeObjectURL(
                    item.preview
                );
            }
        });

        onChange(
            processed.slice(
                0,
                1
            )
        );
    } else {
        // Multiple files mode → append
        onChange(
            [
                ...files,
                ...processed,
            ].slice(
                0,
                maxFiles
            )
        );
    }

    e.target.value = "";
};

    const removeFile = (index) => {
        const item = files[index];

        if (item?.preview) {
            URL.revokeObjectURL(item.preview);
        }

        onChange(
            files.filter(
                (_, i) => i !== index
            )
        );
    };

    return (
        <Box>
            <Text
                fontSize="xs"
                fontWeight="700"
                color="gray.500"
                letterSpacing="1px"
                mb={2}
            >
                {title.toUpperCase()}
            </Text>

            <input
                hidden
                ref={inputRef}
                type="file"
                multiple={maxFiles > 1}
                accept={accept}
                onChange={handleChange}
            />

            <Box
                border="2px dashed"
                borderColor="gray.300"
                borderRadius="12px"
                bg="#F8FAFC"
                py={12}
                textAlign="center"
                cursor="pointer"
                transition=".2s"
                onClick={() =>
                    inputRef.current?.click()
                }
                _hover={{
                    bg: "#F5F8FF",
                    borderColor: "#2453FF",
                }}
            >
                <Flex
                    justify="center"
                    mb={3}
                >
                    <Upload
                        size={28}
                        color="#6B7280"
                    />
                </Flex>

                <Text fontWeight="600">
                    {description}
                </Text>

                <Text
                    mt={1}
                    color="gray.500"
                    fontSize="sm"
                >
                    {maxFiles} file
                    {maxFiles > 1 ? "s" : ""} maximum
                </Text>
            </Box>

            {files.length > 0 && (
                <Grid
                    templateColumns="repeat(2,1fr)"
                    gap={3}
                    mt={4}
                >
                    {files.map(
                        (
                            item,
                            index
                        ) => (
                            <Box
                                key={index}
                                border="1px solid"
                                borderColor="gray.200"
                                borderRadius="12px"
                                p={3}
                            >
                                <Flex gap={3}>
                                    {item.preview ? (
                                        <Box
                                            w="70px"
                                            h="70px"
                                            borderRadius="10px"
                                            overflow="hidden"
                                        >
                                            <img
                                                src={
                                                    item.preview
                                                }
                                                alt=""
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit:
                                                        "cover",
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Flex
                                            w="70px"
                                            h="70px"
                                            bg="gray.100"
                                            borderRadius="10px"
                                            justify="center"
                                            align="center"
                                        >
                                            <FileText
                                                size={
                                                    28
                                                }
                                            />
                                        </Flex>
                                    )}

                                    <Box
                                        flex="1"
                                    >
                                        <Text
                                        fontWeight="600"
                                        fontSize="sm"
                                        display="-webkit-box"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        sx={{
                                            WebkitLineClamp: {
                                                base: 1,
                                                md: 2,
                                            },
                                            WebkitBoxOrient: "vertical",
                                        }}
                                    >
                                        {item.file.name}
                                    </Text>

                                        <Text
                                            color="gray.500"
                                            fontSize="xs"
                                        >
                                            {(
                                                item
                                                    .file
                                                    .size /
                                                1024
                                            ).toFixed(
                                                1
                                            )}{" "}
                                            KB
                                        </Text>
                                    </Box>

                                    <Box
                                        cursor="pointer"
                                        onClick={() =>
                                            removeFile(
                                                index
                                            )
                                        }
                                    >
                                        <X
                                            size={
                                                16
                                            }
                                            color="#EF4444"
                                        />
                                    </Box>
                                </Flex>
                            </Box>
                        )
                    )}
                </Grid>
            )}
        </Box>
    );
};

export default AttachmentUpload;